import { ORDER_SHEET_STATE } from '@theailab-dev/parchment';
import type { OrderSheetState } from '@theailab-dev/parchment';
import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DetailPageLayout } from '../../components/Layout/DetailPageLayout';
import { Badge, Button, Card, InfoItem } from '../../components/ui';
import { useOrderSheetDetail } from '../../hooks/useOrderSheetDetail';

const OrderSheetDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const orderSheetId = id ? Number(id) : NaN;

  const { data, isLoading, isError, error, refetch } = useOrderSheetDetail(
    Number.isNaN(orderSheetId) ? null : orderSheetId,
  );

  const formatDateTime = (date: Date | string | null | undefined) => {
    if (!date) return '-';
    try {
      const dateObj = typeof date === 'string' ? new Date(date) : date;
      if (isNaN(dateObj.getTime())) return '-';

      return dateObj.toLocaleString('ko-KR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return '-';
    }
  };

  const formatAmount = (value: number | string | null | undefined) => {
    if (value === null || value === undefined) return '-';

    const num = typeof value === 'number' ? value : Number.isNaN(Number(value)) ? NaN : Number(value);

    if (!Number.isFinite(num)) return '-';
    return num.toLocaleString();
  };

  const getOrderSheetStateBadgeVariant = (state: OrderSheetState) => {
    switch (state) {
      case ORDER_SHEET_STATE.PAID:
        return 'success';
      case ORDER_SHEET_STATE.PROCESSING:
        return 'primary';
      case ORDER_SHEET_STATE.PARTIALLY_CANCELED:
      case ORDER_SHEET_STATE.PARTIALLY_REFUNDED:
      case ORDER_SHEET_STATE.CANCELED:
      case ORDER_SHEET_STATE.REFUNDED:
        return 'warning';
      case ORDER_SHEET_STATE.PAYMENT_FAILED:
      case ORDER_SHEET_STATE.CANCELLATION_FAILED:
        return 'danger';
      case ORDER_SHEET_STATE.ORDERED:
      default:
        return 'neutral';
    }
  };

  const getOrderSheetStateLabel = (state: OrderSheetState) => {
    switch (state) {
      case ORDER_SHEET_STATE.ORDERED:
        return '주문됨';
      case ORDER_SHEET_STATE.PAID:
        return '결제 완료';
      case ORDER_SHEET_STATE.PROCESSING:
        return '처리 중';
      case ORDER_SHEET_STATE.CANCELED:
      case ORDER_SHEET_STATE.REFUNDED:
        return '취소됨';
      case ORDER_SHEET_STATE.PARTIALLY_CANCELED:
      case ORDER_SHEET_STATE.PARTIALLY_REFUNDED:
        return '부분 취소/환불 됨';
      case ORDER_SHEET_STATE.PAYMENT_FAILED:
      case ORDER_SHEET_STATE.CANCELLATION_FAILED:
        return '결제/취소 에러';
      default:
        return state;
    }
  };

  const handleGoUserDetail = async () => {
    if (!data) return;
    await navigate(`/dashboard/users/${data.accountId}`);
  };

  return (
    <DetailPageLayout
      title="주문서 상세"
      subtitle={data ? `주문서 ID: ${data.id}` : undefined}
      isLoading={isLoading}
      isError={isError}
      errorMessage={error instanceof Error ? error.message : undefined}
      onRetry={refetch}
      onBack={() => navigate(-1)}>
      {data && (
        <div className="space-y-6">
          {/* 주문서 정보 */}
          <Card title="주문서 정보">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <InfoItem label="주문서 ID" value={`#${data.id}`} />
              <InfoItem
                label="주문 상태"
                value={
                  <Badge variant={getOrderSheetStateBadgeVariant(data.state)} className="whitespace-nowrap">
                    {getOrderSheetStateLabel(data.state)}
                  </Badge>
                }
              />
              <InfoItem label="주문 제목" value={data.title} />
              <InfoItem label="주문일시" value={formatDateTime(data.createdAt)} />
              <InfoItem label="최종 수정일시" value={formatDateTime(data.updatedAt)} />
            </div>

            {/* 기술 정보 */}
            {(data.checkoutId || data.merchantUid) && (
              <div className="mt-6 border-t border-slate-200 pt-6">
                <div className="mb-3 text-sm font-medium text-slate-700">기술 정보</div>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  {data.checkoutId && (
                    <div>
                      <label className="mb-1 block text-xs text-slate-500">체크아웃 ID</label>
                      <span className="font-mono text-sm text-slate-900">{data.checkoutId}</span>
                    </div>
                  )}
                  {data.merchantUid && (
                    <div>
                      <label className="mb-1 block text-xs text-slate-500">PG 주문 ID</label>
                      <span className="font-mono text-sm text-slate-900">{data.merchantUid}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </Card>

          {/* 구매자 정보 */}
          <Card
            title="구매자 정보"
            actions={
              <Button size="sm" variant="outline" onClick={handleGoUserDetail}>
                사용자 상세로 이동
              </Button>
            }>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <InfoItem label="구매자 이름" value={data.buyerName} />
              <InfoItem label="구매자 이메일" value={data.buyerEmail} />
              <InfoItem label="구매자 전화번호" value={data.buyerPhone} />
              <InfoItem label="구매자 계정 ID" value={`#${data.accountId}`} />
            </div>
          </Card>

          {/* 주문 금액 */}
          <Card title="주문 금액">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <InfoItem label="전체 원가" value={`${formatAmount(data.originalPrice)}원`} />
              {data.discount > 0 && <InfoItem label="전체 할인 금액" value={`${formatAmount(data.discount)}원`} />}
              <InfoItem
                label="최종 주문 금액"
                value={<span className="text-lg font-semibold text-slate-900">{formatAmount(data.finalPrice)}원</span>}
              />
            </div>
          </Card>
        </div>
      )}
    </DetailPageLayout>
  );
};

export default OrderSheetDetail;
